import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ProblemList } from "../components/ProblemList";
import { ClosingCTA } from "../components/ClosingCTA";
import { SectionLabel } from "../components/SectionLabel";
import { hrCapabilities, systemTypes, systemSide, operationSide } from "../data/hrTechnology";
import { SystemEcosystem } from "../components/hrtech/SystemEcosystem";
import { SystemStateComparison } from "../components/hrtech/SystemStateComparison";
import { ImplementationTimeline } from "../components/hrtech/ImplementationTimeline";
import { EditorialImage } from "../components/EditorialImage";
import { photos } from "../data/photography";
import { ledgerEase } from "../utils/motion";
export function HrTechnology() {
  return <>
      <section aria-labelledby="hrtech-title" className="shell pb-20 pt-14 md:pb-28 md:pt-20" data-id="element-735" data-mp="2nwsitp8nr9z6">
        <div className="grid grid-cols-12 items-end gap-x-6 gap-y-12" data-id="element-736" data-mp="2iyqscj8wk8do">
          <div className="col-span-12 lg:col-span-7 lg:pb-10 lg:pr-6" data-id="element-737" data-mp="2j0k2w9mmzcqy">
            <SectionLabel data-id="element-738" data-mp="q6776h27vn7l">HR Technology</SectionLabel>
            <motion.h1 id="hrtech-title" initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease: ledgerEase
          }} className="mt-8 font-display text-[clamp(2.75rem,5.4vw,5.25rem)] leading-[1.02] tracking-[-0.015em] text-navy" data-id="element-739" data-mp="v50iq3xy1454">
              Systems that work the way your business actually works.
            </motion.h1>
            <p className="mt-10 max-w-xl text-lg leading-relaxed text-charcoal/80 md:text-xl" data-id="element-740" data-mp="cfafqtk11c24">
              We implement, repair, configure, optimize, and support the HR and payroll technology your organization
              depends on.
            </p>
            <Link to="/contact" className="btn-primary mt-10" data-id="element-741" data-mp="3kvdq572ixeu5">
              Talk to Us
            </Link>
          </div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.3,
          ease: ledgerEase
        }} className="col-span-12 lg:col-span-5" data-id="element-742" data-mp="3ove46qlpno0o">
            <EditorialImage image={photos.hrtechArchitecture} caption="Every line connects to another. Nothing is left floating." priority data-id="element-743" data-mp="r2m318fg6qjn" />
          </motion.div>
        </div>
      </section>

      <section aria-labelledby="hrtech-capabilities" className="shell pb-24 md:pb-32" data-id="element-744" data-mp="d2us5gk89q9v">
        <h2 id="hrtech-capabilities" className="sr-only" data-id="element-745" data-mp="8403hxzkx7hu">
          What we do in HR technology
        </h2>
        <ol className="grid grid-cols-1 border-l border-t border-stone border-t-navy sm:grid-cols-2 lg:grid-cols-4" data-id="element-746" data-mp="m3jb8pohagtm">
          {hrCapabilities.map(c => <li key={c.name} className="border-b border-r border-stone p-8" data-id="element-747" data-mp="i0wd6717rznz">
              <span className="text-sm tabular-nums text-oxblood" data-id="element-748" data-mp="1xhn05hg7lert">{c.number}</span>
              <h3 className="mt-6 font-display text-2xl leading-tight text-navy" data-id="element-749" data-mp="1ti4jixvf7pus">{c.name}</h3>
              <p className="mt-4 text-[15px] leading-relaxed text-charcoal/75" data-id="element-750" data-mp="210m4q1cnst3l">{c.line}</p>
            </li>)}
        </ol>
      </section>

      <SystemEcosystem data-id="element-751" data-mp="25xvr184r4on7" />

      <section aria-labelledby="systems-title" className="shell pb-24 md:pb-32" data-id="element-752" data-mp="1r2po4u0yd5y3">
        <div className="grid grid-cols-12 items-start gap-x-6 gap-y-12" data-id="element-753" data-mp="1r52bjmphc8ij">
          <div className="col-span-12 lg:col-span-6" data-id="element-754" data-mp="1w2atpnamocjs">
            <EditorialImage image={photos.hrtechHands} aspect="aspect-[3/2]" caption="Configuration, read line by line — by people who also know how payroll runs." data-id="element-755" data-mp="2jqttfefm3h09" />
          </div>
          <div className="col-span-12 lg:col-span-5 lg:col-start-8" data-id="element-756" data-mp="2a0scd6loeyuu">
            <div className="border-t-2 border-oxblood pt-6" data-id="element-757" data-mp="2ezva05zbu2ss">
              <p className="eyebrow text-oxblood" data-id="element-758" data-mp="2eyaeoe56uftv">Systems we work in</p>
              <h2 id="systems-title" className="mt-5 font-display text-[clamp(2rem,3.2vw,2.75rem)] leading-[1.08] text-navy" data-id="element-759" data-mp="32ooc6k6jl6vu">
                Vendor-agnostic. Inside the platform you have.
              </h2>
              <ul className="mt-8 border-t border-stone" data-id="element-760" data-mp="1jtxq1k0uuu3h">
                {systemTypes.map(s => <li key={s.name} className="grid grid-cols-5 gap-4 border-b border-stone py-3" data-id="element-761" data-mp="1es8dfzxzi8da">
                    <span className="col-span-2 font-display text-lg text-navy" data-id="element-762" data-mp="19uouwnu979fj">{s.name}</span>
                    <span className="col-span-3 text-sm leading-6 text-charcoal/70" data-id="element-763" data-mp="14x6k0rwoek4w">{s.line}</span>
                  </li>)}
              </ul>
              <p className="mt-6 text-sm leading-relaxed text-charcoal/75" data-id="element-764" data-mp="1xjrbdsd5iw8g">
                Or we’ll help you choose the next one — without a reseller’s interest in the answer.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section aria-labelledby="both-sides" className="bg-navy text-ivory" data-id="element-765" data-mp="1nsdiw4jmawot">
        <div className="shell py-24 md:py-32" data-id="element-766" data-mp="2lb6ma9izulvb">
          <div className="grid grid-cols-12 gap-x-6 gap-y-14" data-id="element-767" data-mp="2gj5a0nltsv5a">
            <div className="col-span-12 lg:col-span-5" data-id="element-768" data-mp="2ggsipju6h8fk">
              <p className="eyebrow text-ivory/75" data-id="element-769" data-mp="2oskt6uxu2150">Why it holds up</p>
              <h2 id="both-sides" className="mt-8 font-display text-[clamp(2.25rem,4vw,3.5rem)] leading-[1.06]" data-id="element-770" data-mp="2ssuye5dswnit">
                We work in the systems — and we understand the work they support.
              </h2>
              <p className="mt-6 max-w-md leading-relaxed text-ivory/70" data-id="element-771" data-mp="32rtz91sbrev9">
                That’s why a configuration fix from Constant still holds up on payday.
              </p>
            </div>
            <div className="col-span-12 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:col-span-6 lg:col-start-7" data-id="element-772" data-mp="3brgypi1hualo">
              {[{
              title: "In the system",
              items: systemSide
            }, {
              title: "In the operation",
              items: operationSide
            }].map(col => <div key={col.title} data-id="element-773" data-mp="3qh52cpx5dlck">
                  <p className="font-display text-2xl italic" data-id="element-774" data-mp="3qi6b2nqoxvr4">{col.title}</p>
                  <ul className="mt-6 border-t border-ivory/30" data-id="element-775" data-mp="3vf6m7d2edop3">
                    {col.items.map(item => <li key={item} className="border-b border-ivory/15 py-3 text-[15px] text-ivory/85" data-id="element-776" data-mp="35sm021uu7l7l">
                        {item}
                      </li>)}
                  </ul>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      <SystemStateComparison data-id="element-777" data-mp="3aq5fv676o61n" />
      <ImplementationTimeline data-id="element-778" data-mp="3ap2n0sqavtj3" />

      <ProblemList area="hrtech" title="HR technology problems we’re called for." data-id="element-779" data-mp="3ohskpuf6ymw1" />
      <ClosingCTA data-id="element-780" data-mp="3jj8w1m97qwzz" />
    </>;
}